import React, { useState, useEffect, useMemo, useCallback } from "react";
import CalendarHeader from "../components/CalendarHeader";
import CalendarGrid from "../components/CalendarGrid";
import {
  getBookings,
  BookingsResponse,
  getBookingEdit,
} from "../services/bookings";
import { Booking } from "../types/booking";
import GlobalLoader from "../components/ui/GlobalLoader";
import { useEmployees } from "../hooks/useEmployees";
import { useToast } from "../hooks/useToast";

import { startOfDayISO } from "../lib/dateUtil";

const BookingsPage: React.FC = () => {
  const toast = useToast();
  const [realBookings, setRealBookings] = useState<Booking[]>([]);
  const [mockBookings, setMockBookings] = useState<Booking[]>([]);

  const [startDate, setStartDate] = useState<string>(
    startOfDayISO(new Date())
  );
  const [endDate, setEndDate] = useState<string>(
    startOfDayISO(new Date())
  );
  const [status, setStatus] = useState<string>("all");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const { staffList, loading: employeesLoading } = useEmployees();
  const [staffLoaded, setStaffLoaded] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<string | null>("");
  const [calendarView, setCalendarView] = useState<"day" | "week">("day");
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refreshBookings = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (staffList.length > 0) {
      setStaffLoaded(true);
    }
  }, [staffList]);
  const staticStaff = useMemo(
    () =>
      [
        "Alice Williams",
        "Bob Smith",
        "Charlie Brown",
        "David Davis",
        "Eve Miller",
        "Frank Wilson",
        "Grace Moore",
        "Hank Taylor",
        "Ivy Anderson",
        "Jack Thomas",
      ].map((name, i) => ({
        id: 1000 + i,
        name,
        color: [
          "#885ABB",
          "#5AB7BB",
          "#C96EBE",
          "#837EED",
          "#FFB74D",
          "#4DB6AC",
          "#E57373",
        ][i % 7],
      })),
    []
  );

  const mergedStaffList = useMemo(() => [...staticStaff], [staticStaff]);

  useEffect(() => {
    const fetchRealBookings = async () => {
      if (!startDate || !endDate) return;

      setLoading(true);
      setError("");

      try {
        const response: BookingsResponse = await getBookings({
          filter_status: status,
          startDate,
          endDate,
        });

        const bookingIds = response.data.map((b: any) => b.id);
        const bookingEdits = await Promise.all(
          bookingIds.map((id: number) => getBookingEdit(id))
        );

        const uiBookings: Booking[] = response.data.map(
          (b: any, index: number) => {
            const edit = bookingEdits[index]?.booking;
            // TODO: FIXME: The /admin/bookings API currently returns incorrect booking times.
            // We are temporarily using the time from the /admin/bookings/{id}/edit API because it returns the correct time.
            // This is a workaround and should be reverted/cleaned up once the main bookings API is fixed.
            const startRaw = edit?.date_time || edit?.start || b.date_time;
            let start: Date;
            if (startRaw) {
              // TODO: FIXME: The API incorrectly appends 'Z' (UTC marker) to local times.
              // We need to strip the 'Z' and parse as local time, not UTC.
              // This should be fixed on the backend to send proper local datetime without 'Z'.

              // Strip 'Z' and microseconds if present (e.g., "2026-01-01 13:09:00.000000Z" -> "2026-01-01 13:09:00")
              let cleanedTime = startRaw.replace(/\.?\d{0,6}Z?$/, '');

              // Parse as LOCAL wall-clock time
              const [dPart, tPart] = cleanedTime.split(/[ T]/);
              const [y, month, d] = dPart.split("-").map(Number);
              // Strip non-numeric from time parts (e.g., "00 am" -> "00")
              const timeParts = (tPart || "00:00")
                .split(":")
                .map((part: string) => part.replace(/[^0-9]/g, ""));
              const h = Number(timeParts[0] || 0);
              const m = Number(timeParts[1] || 0);

              // Handle 12-hour format if am/pm is present in the original string
              let finalH = h;
              if (tPart && tPart.toLowerCase().includes("pm") && h < 12)
                finalH += 12;
              if (tPart && tPart.toLowerCase().includes("am") && h === 12)
                finalH = 0;

              start = new Date(y, month - 1, d, finalH, m);
            } else {
              start = new Date();
            }

            // Calculate total duration from all services in the booking
            // Each item has business_service.time and business_service.time_type ('hours' or 'minutes')
            let totalMinutes = 0;

            if (edit?.items && Array.isArray(edit.items)) {
              edit.items.forEach((item: any) => {
                const serviceTime = item?.business_service?.time || 0;
                const timeType = item?.business_service?.time_type || 'minutes';

                if (timeType === 'hours') {
                  totalMinutes += serviceTime * 60; // Convert hours to minutes
                } else {
                  totalMinutes += serviceTime; // Already in minutes
                }
              });
            }

            // Fallback to business_services if items not available
            if (totalMinutes === 0 && bookingEdits[index]?.business_services?.[0]?.time) {
              const serviceTime = bookingEdits[index].business_services[0].time;
              const timeType = bookingEdits[index].business_services[0].time_type || 'minutes';

              if (timeType === 'hours') {
                totalMinutes = serviceTime * 60;
              } else {
                totalMinutes = serviceTime;
              }
            }

            // Final fallback to 60 minutes if still no duration found
            if (totalMinutes === 0) {
              totalMinutes = 60;
            }

            const end = new Date(start.getTime() + totalMinutes * 60000);

            const staffName = edit?.staffName || b.employees?.[0]?.name || "";

            const categoryColor = edit?.items?.[0]?.business_service?.category?.color_code || null;

            // Normalize status to match BookingCard expectations
            let normalizedStatus: "booked" | "completed" | "pending" | "in progress" | "no show" | "cancelled" = "booked";
            const rawStatus = (b.status || "").toLowerCase();

            if (rawStatus === "completed" || rawStatus === "complete") {
              normalizedStatus = "completed";
            } else if (rawStatus === "pending") {
              normalizedStatus = "pending";
            } else if (rawStatus === "in progress" || rawStatus === "in_progress") {
              normalizedStatus = "in progress";
            } else if (rawStatus === "no show" || rawStatus === "no_show") {
              normalizedStatus = "no show";
            } else if (rawStatus === "cancelled" || rawStatus === "canceled") {
              normalizedStatus = "cancelled";
            } else {
              // Default to 'booked' for 'approved', 'booked', 'confirmed', etc.
              normalizedStatus = "booked";
            }

            const mapped = {
              id: b.id,
              client: b.name,
              start: start.toISOString(),
              end: end.toISOString(),
              staff: staffName,
              status: normalizedStatus,
              amountPaid: b.amount_paid,
              amountToPay: b.amount_to_pay,
              categoryColor: categoryColor,
            };

            return mapped;
          }
        );

        setRealBookings(uiBookings);
      } catch (err) {
        console.error("API error fetching real bookings:", err);
        toast.error("Failed to load calendar bookings");
      } finally {
        setLoading(false);
        setHasLoadedOnce(true);
      }
    };

    fetchRealBookings();
  }, [startDate, endDate, status, refreshTrigger]);

  // STATIC MOCK DATA GENERATION
  useEffect(() => {
    const generateStaticData = () => {
      const generated: Booking[] = [];
      const durations = [30, 45, 60, 90, 120];

      staticStaff.forEach((staff) => {
        const baseDate = new Date(calendarDate);
        baseDate.setHours(9, 0, 0, 0);
        let currentTime = new Date(baseDate);

        const endTimeLimit = new Date(baseDate);
        endTimeLimit.setHours(21, 0, 0, 0);

        let idx = 0;
        while (currentTime < endTimeLimit) {
          const shouldSkip = Math.random() < 0.6;
          const duration =
            durations[Math.floor(Math.random() * durations.length)];
          const start = new Date(currentTime);
          const end = new Date(start.getTime() + duration * 60000);

          if (end > endTimeLimit) break;

          if (!shouldSkip) {
            const statusOptions: ("booked" | "completed" | "pending")[] = [
              "booked",
              "completed",
              "pending",
            ];
            const randomStatus =
              statusOptions[Math.floor(Math.random() * statusOptions.length)];

            generated.push({
              id: 999000 + staff.id * 100 + idx,
              client: `${staff.name.split(" ")[0]}'s Client ${idx + 1}`,
              start: start.toISOString(),
              end: end.toISOString(),
              staff: staff.name,
              status: randomStatus,
              amountPaid: 20 * (idx + 1),
              amountToPay: 20 * (idx + 1),
              currency: "$",
              categoryColor: ["#885ABB", "#5AB7BB", "#C96EBE", "#837EED", "#FFB74D", "#4DB6AC", "#E57373"][Math.floor(Math.random() * 7)],
            });

            const gap = (1 + Math.floor(Math.random() * 3)) * 15;
            currentTime = new Date(end.getTime() + gap * 60000);
          } else {
            const jump = (3 + Math.floor(Math.random() * 6)) * 15;
            currentTime = new Date(currentTime.getTime() + jump * 60000);
          }
          idx++;
        }
      });
      setMockBookings(generated);
    };

    generateStaticData();
  }, [calendarDate]);

  // COMBINE BOTH FOR THE GRID
  const allBookings = useMemo(
    () => [...realBookings, ...mockBookings],
    [realBookings, mockBookings]
  );

  return (
    <div>
      <div className="min-h-screen bg-gray-100">
        <CalendarHeader
          startDate={startDate}
          endDate={endDate}
          status={status}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onStatusChange={setStatus}
          staffList={staffList}
          onStaffChange={setSelectedStaff}
          view={calendarView}
          setView={setCalendarView}
          selectedDate={calendarDate}
          setSelectedDate={setCalendarDate}
        />

        <div className="relative">
          {error && <p className="text-red-500 p-4">{error}</p>}

          {!hasLoadedOnce || !staffLoaded ? (
            <GlobalLoader
              initialPageLoad={true}
              description="Loading calendar..."
              compensateSidebar={true}
            />
          ) : (
            <>
              <CalendarGrid
                // bookings={allBookings}
                bookings={realBookings}
                selectedStaff={selectedStaff}
                view={calendarView}
                selecteddate={calendarDate}
                // staffList={mergedStaffList}
                staffList={staffList}
                onRefresh={refreshBookings}
              />
              {loading && (
                <GlobalLoader
                  initialPageLoad={true}
                  description="Updating calendar..."
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingsPage;
